import * as THREE from 'three';

export default class ScaleFormularController {
  constructor(
    defaultDimensions = {},
    defaultOptions = {},
    defaultThicknesses = {},
    moduleFormular = [],
    onFormularChange = true
  ) {
    //Công thức scale cho module
    this.moduleFormular = moduleFormular;

    // Kích thước mặc định (đơn vị mét)
    this.defaultDimensions = {
      ...defaultDimensions,
    };

    // Các tùy chọn mặc định
    this.defaultOptions = {
      ...defaultOptions,
    };

    // Các độ dày mặc định
    this.defaultThicknesses = {
      DDTBia: this?.defaultOptions?.DDTBia ?? 0.017,
      DDTHau: this?.defaultOptions?.DDTHau ?? 0.008,
      ...defaultThicknesses,
    };

    // Current values
    this.currentDimensions = { ...this.defaultDimensions };
    this.currentOptions = { ...this.defaultOptions };
    this.currentThicknesses = { ...this.defaultThicknesses };

    // Callback for when formular changes
    this.onFormularChange = onFormularChange;

    // Bind methods
    this.evaluateFormula = this.evaluateFormula.bind(this);
    this.calculateOriginalMeshSizes =
      this.calculateOriginalMeshSizes.bind(this);
    this.applyFormulaScaling = this.applyFormulaScaling.bind(this);
    // this.updateModuleFormula = this.updateModuleFormula.bind(this);
  }

  // Thêm vào trong class ScaleFormularController
  clone() {
    // Tạo instance mới với constructor
    const cloned = new ScaleFormularController(
      { ...this.defaultDimensions },
      { ...this.defaultOptions },
      { ...this.defaultThicknesses },
      // Deep clone array of formulas
      this.moduleFormular.map(formula => ({ ...formula })),
      this.onFormularChange
    );

    // Copy current state
    cloned.currentDimensions = { ...this.currentDimensions };
    cloned.currentOptions = { ...this.currentOptions };
    cloned.currentThicknesses = { ...this.currentThicknesses };

    return cloned;
  }

  // Getter methods
  getDimensions() {
    return { ...this.currentDimensions };
  }

  getOptions() {
    return { ...this.currentOptions };
  }

  getThicknesses() {
    return { ...this.currentThicknesses };
  }

  getCurrentVariables() {
    return {
      ...this.currentOptions,
      ...this.currentDimensions,
      ...this.currentThicknesses,
    };
  }

  // Setter methods
  setDimensions(newDimensions) {
    this.currentDimensions = { ...this.currentDimensions, ...newDimensions };
    if (this.onFormularChange) {
      this.onFormularChange(this.getCurrentVariables());
    }
  }

  setOptions(newOptions) {
    this.currentOptions = { ...this.currentOptions, ...newOptions };
    if (this.onFormularChange) {
      this.onFormularChange(this.getCurrentVariables());
    }
  }

  setThicknesses(newThicknesses) {
    this.currentThicknesses = { ...this.currentThicknesses, ...newThicknesses };
    if (this.onFormularChange) {
      this.onFormularChange(this.getCurrentVariables());
    }
  }

  // Update individual values
  updateDimension(key, value) {
    this.currentDimensions[key] = value;

    // if (this.onFormularChange) {
    //   this.onFormularChange(this.getCurrentVariables());
    // }
  }

  updateOption(key, value) {
    this.currentOptions[key] = value;
    if (this.onFormularChange) {
      this.onFormularChange(this.getCurrentVariables());
    }
  }

  updateThickness(key, value) {
    this.currentThicknesses[key] = value;
    if (this.onFormularChange) {
      this.onFormularChange(this.getCurrentVariables());
    }
  }

  // Reset methods
  resetToDefaults() {
    this.currentDimensions = { ...this.defaultDimensions };
    this.currentOptions = { ...this.defaultOptions };
    this.currentThicknesses = { ...this.defaultThicknesses };
    if (this.onFormularChange) {
      this.onFormularChange(this.getCurrentVariables());
    }
  }

  resetDimensions() {
    this.currentDimensions = { ...this.defaultDimensions };
    if (this.onFormularChange) {
      this.onFormularChange(this.getCurrentVariables());
    }
  }

  resetOptions() {
    this.currentOptions = { ...this.defaultOptions };
    if (this.onFormularChange) {
      this.onFormularChange(this.getCurrentVariables());
    }
  }

  resetThicknesses() {
    this.currentThicknesses = { ...this.defaultThicknesses };
    if (this.onFormularChange) {
      this.onFormularChange(this.getCurrentVariables());
    }
  }

  // Formula evaluation
  evaluateFormula(formula, customVariables = null) {
    try {
      const variables = customVariables || this.getCurrentVariables();
      let expression = formula;

      for (const [key, value] of Object.entries(variables)) {
        const regex = new RegExp(`\\b${key}\\b`, 'g');
        expression = expression.replace(regex, value.toString());
      }

      return Function(`"use strict"; return (${expression})`)();
    } catch (error) {
      console.warn(`Error evaluating formula: ${formula}`, error);
      return 0;
    }
  }

  // Calculate mesh results for all formulas
  calculateMeshResults(customVariables = null) {
    const variables = customVariables || this.getCurrentVariables();
    const results = {};

    this.moduleFormular.forEach(formula => {
      results[formula.mesh_name] = {
        _id: formula._id,
        nameMesh: formula.mesh_name,
        position: {
          x: this.evaluateFormula(formula.position_x, variables),
          y: this.evaluateFormula(formula.position_y, variables),
          z: this.evaluateFormula(formula.position_z, variables),
        },
        size: {
          x: this.evaluateFormula(formula.size_x, variables),
          y: this.evaluateFormula(formula.size_y, variables),
          z: this.evaluateFormula(formula.size_z, variables),
        },
        scale: {
          x: this.evaluateFormula(formula.scale_x, variables),
          y: this.evaluateFormula(formula.scale_y, variables),
          z: this.evaluateFormula(formula.scale_z, variables),
        },
        rotation: {
          x: this.evaluateFormula(formula.rotation_x, variables),
          y: this.evaluateFormula(formula.rotation_y, variables),
          z: this.evaluateFormula(formula.rotation_z, variables),
        },
      };
    });

    return results;
  }

  // Get formula by mesh name
  getFormulaByMeshName(meshName) {
    return this.moduleFormular.find(f => f.mesh_name === meshName);
  }

  // Get all formulas
  getAllFormulas() {
    return [...this.moduleFormular];
  }

  // Tính kích thước ban đầu của tất cả mesh
  calculateOriginalMeshSizes(scene) {
    const meshSizeMap = new Map();

    scene.traverse(child => {
      if (child.isMesh || child.isGroup) {
        // Reset về transform gốc để tính kích thước thực
        const originalScale = child.scale.clone();
        const originalPosition = child.position.clone();
        const originalRotation = child.rotation.clone();

        // Reset transform
        child.scale.set(1, 1, 1);
        child.position.set(0, 0, 0);
        child.rotation.set(0, 0, 0);
        child.updateMatrixWorld(true);

        // Tính bounding box với transform đã reset
        const bbox = new THREE.Box3().setFromObject(child);
        const originalSize = new THREE.Vector3();
        bbox.getSize(originalSize);
        const center = new THREE.Vector3();
        bbox.getCenter(center);

        // Khôi phục transform gốc
        child.scale.copy(originalScale);
        child.position.copy(originalPosition);
        child.rotation.copy(originalRotation);
        child.updateMatrixWorld(true);

        meshSizeMap.set(child.name, {
          mesh: child,
          originalSize: originalSize,
          originalCenter: center,
          originalPosition: originalPosition.clone(),
          originalScale: originalScale.clone(),
          originalRotation: originalRotation.clone(),
        });
      }
    });

    return meshSizeMap;
  }

  // Áp dụng công thức scaling cho module
  applyFormulaScaling(module, mdDimensions = {}, mdOptions = {}) {
    // Cập nhật trực tiếp các giá trị hiện tại
    this.currentOptions = { ...this.currentOptions, ...mdOptions };
    this.currentDimensions = { ...this.currentDimensions, ...mdDimensions };

    if (mdDimensions?.thickness) {
      this.currentThicknesses = {
        ...this.currentThicknesses,
        DDTBia: mdDimensions.thickness,
        // DDTHau: this.defaultOptions?.DDTHau ?? mdOptions?.DDTHau ?? 0.0008,
      };
    }

    if (mdOptions?.DDTHau) {
      this.currentThicknesses = {
        ...this.currentThicknesses,
        DDTHau: mdOptions.DDTHau ?? 0.0008,
      };
    }

    // Tạo object chứa tất cả biến từ các giá trị đã cập nhật
    const variables = {
      ...this.currentOptions,
      ...this.currentDimensions,
      ...this.currentThicknesses,
    };

    // Lưu trạng thái hiện tại của module
    const originalModulePosition = module.position.clone();
    const originalModuleRotation = module.rotation.clone();
    const originalModuleScale = module.scale.clone();

    // Reset module về trạng thái mặc định để tính toán chính xác
    module.position.set(0, 0, 0);
    module.rotation.set(0, 0, 0);
    module.scale.set(1, 1, 1);
    module.updateMatrixWorld(true);

    // Tính kích thước ban đầu của tất cả mesh trong trạng thái mặc định
    const originalMeshes = this.calculateOriginalMeshSizes(module);

    // Áp dụng công thức cho từng mesh
    this.moduleFormular.forEach(formula => {
      const meshData = originalMeshes.get(formula.mesh_name);

      if (meshData) {
        const mesh = meshData.mesh;
        const originalSize = meshData.originalSize;
        const originalCenter = meshData.originalCenter;

        // Tính toán kích thước mới từ công thức
        const newSizeX = this.evaluateFormula(formula.size_x, variables);
        const newSizeY = this.evaluateFormula(formula.size_y, variables);
        const newSizeZ = this.evaluateFormula(formula.size_z, variables);

        // Tính toán vị trí mới từ công thức
        const newPosX = this.evaluateFormula(formula.position_x, variables);
        const newPosY = this.evaluateFormula(formula.position_y, variables);
        const newPosZ = this.evaluateFormula(formula.position_z, variables);

        // Tính toán rotation mới từ công thức
        const newRotX = this.evaluateFormula(formula.rotation_x, variables);
        const newRotY = this.evaluateFormula(formula.rotation_y, variables);
        const newRotZ = this.evaluateFormula(formula.rotation_z, variables);

        // Tính toán scale factor dựa trên kích thước ban đầu thực tế
        const scale_x = originalSize.x !== 0 ? newSizeX / originalSize.x : 1;
        const scale_y = originalSize.y !== 0 ? newSizeY / originalSize.y : 1;
        const scale_z = originalSize.z !== 0 ? newSizeZ / originalSize.z : 1;

        // Reset mesh về trạng thái gốc trước khi áp dụng transform mới
        mesh.scale.set(1, 1, 1);
        mesh.position.set(0, 0, 0);
        mesh.rotation.set(0, 0, 0);

        // Áp dụng transformations trong local space của mesh (trong scene đã reset)

        // 1. Áp dụng scale
        mesh.scale.set(scale_x, scale_y, scale_z);

        // 2. Áp dụng rotation
        mesh.rotation.order = 'XYZ';
        mesh.rotation.set(newRotX, newRotY, newRotZ);

        // 3. Áp dụng position
        mesh.position.set(newPosX, newPosY, newPosZ);

        // Cập nhật matrix transformation cho mesh
        mesh.updateMatrix();
        mesh.updateMatrixWorld(true);

        // Lưu thông tin vào userData
        mesh.userData = {
          ...mesh.userData,
          formulaId: formula._id,
          nameMesh: formula.mesh_name,
          originalThickness: this.defaultThicknesses,
          originalSize: originalSize.clone(),
          originalCenter: originalCenter.clone(),
          currentThichness: this.currentThicknesses,
          newSize: new THREE.Vector3(newSizeX, newSizeY, newSizeZ),
          scaleFactors: new THREE.Vector3(scale_x, scale_y, scale_z),
          localPosition: new THREE.Vector3(newPosX, newPosY, newPosZ),
          localRotation: new THREE.Vector3(newRotX, newRotY, newRotZ),
          formulaApplied: true,
        };
      } else {
        console.warn(`Mesh "${formula.mesh_name}" not found in model`);
      }
    });

    // Khôi phục lại trạng thái ban đầu của module
    module.position.copy(originalModulePosition);
    module.rotation.copy(originalModuleRotation);
    module.scale.copy(originalModuleScale);

    const moduleData = module.userData.moduleData ?? {};
    moduleData.width = this.currentDimensions.width ?? moduleData.width;
    moduleData.height = this.currentDimensions.height ?? moduleData.height;
    moduleData.depth = this.currentDimensions.depth ?? moduleData.depth;
    moduleData.rongK1 =
      this?.currentOptions?.rongK1 ?? moduleData?.options?.rongK1;
    moduleData.rongK2 =
      this?.currentOptions?.rongK2 ?? moduleData?.options?.rongK2;
    module.userData = {
      ...module.userData,
      moduleData,
    };
    module.updateMatrixWorld(true);

    return module;
  }

  // Cập nhật công thức cho module đã load
  // updateModuleFormula(moduleGroup, mdDimensions = {}, mdOptions = {}) {
  //   const options = { ...this.currentOptions, ...mdOptions };
  //   const dimensions = { ...this.currentDimensions, ...mdDimensions };
  //   const thicknesses = { ...this.currentThicknesses };

  //   const variables = {
  //     ...options,
  //     ...dimensions,
  //     ...thicknesses,
  //   };

  //   moduleGroup.traverse(child => {
  //     if (
  //       child.isMesh &&
  //       child.userData.formulaId &&
  //       child.userData.originalSize
  //     ) {
  //       const formula = this.moduleFormular.find(
  //         f => f._id === child.userData.formulaId
  //       );

  //       if (formula) {
  //         const originalSize = child.userData.originalSize;

  //         // Tính toán lại kích thước, vị trí và rotation từ công thức
  //         const newSizeX = this.evaluateFormula(formula.size_x, variables);
  //         const newSizeY = this.evaluateFormula(formula.size_y, variables);
  //         const newSizeZ = this.evaluateFormula(formula.size_z, variables);

  //         const newPosX = this.evaluateFormula(formula.position_x, variables);
  //         const newPosY = this.evaluateFormula(formula.position_y, variables);
  //         const newPosZ = this.evaluateFormula(formula.position_z, variables);

  //         const newRotX = this.evaluateFormula(formula.rotation_x, variables);
  //         const newRotY = this.evaluateFormula(formula.rotation_y, variables);
  //         const newRotZ = this.evaluateFormula(formula.rotation_z, variables);

  //         // Tính toán scale factor dựa trên kích thước ban đầu thực tế
  //         const scale_x = originalSize.x !== 0 ? newSizeX / originalSize.x : 1;
  //         const scale_y = originalSize.y !== 0 ? newSizeY / originalSize.y : 1;
  //         const scale_z = originalSize.z !== 0 ? newSizeZ / originalSize.z : 1;

  //         // Reset về trạng thái gốc trước khi áp dụng transform mới
  //         child.scale.set(1, 1, 1);
  //         child.position.set(0, 0, 0);
  //         child.rotation.set(0, 0, 0);

  //         // Áp dụng scale, position và rotation mới
  //         child.scale.set(scale_x, scale_y, scale_z);
  //         child.position.set(newPosX, newPosY, newPosZ);
  //         child.rotation.set(newRotX, newRotY, newRotZ);

  //         // Cập nhật matrix
  //         child.updateMatrixWorld(true);

  //         // Cập nhật userData
  //         child.userData.newSize = new THREE.Vector3(
  //           newSizeX,
  //           newSizeY,
  //           newSizeZ
  //         );
  //         child.userData.scaleFactors = new THREE.Vector3(
  //           scale_x,
  //           scale_y,
  //           scale_z
  //         );
  //         child.userData.rotation = new THREE.Vector3(
  //           newRotX,
  //           newRotY,
  //           newRotZ
  //         );
  //       }
  //     }
  //   });
  // }

  // Validate formula syntax
  validateFormula(formula) {
    try {
      const testVariables = this.getCurrentVariables();
      this.evaluateFormula(formula, testVariables);
      return { valid: true, error: null };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  }

  // Get mesh names
  getMeshNames() {
    return this.moduleFormular.map(f => f.mesh_name);
  }

  // Setup callback
  setOnFormularChange(callback) {
    this.onFormularChange = callback;
  }

  // Cleanup
  dispose() {
    this.onFormularChange = null;
  }
}
